import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

// Marca por organización aplicada a TODA la página (login, shell, lecciones).
//
// Fuentes, en este orden:
//   0. La org de la ruta de login (/o/:slug/login), mientras esa pantalla está montada.
//   1. La org del usuario logueado (redux `company`), cuando ya cargó.
//   2. La org dueña del dominio por el que entró (`GET /auth/org/by-host`),
//      para pintar el login antes de que exista sesión.
// Sin ninguna de las dos la plataforma queda con el tema Ewaffle de siempre:
// las variables CSS de abajo tienen los valores históricos como default.

let apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
  apiUrl = `https://${apiUrl}`;
}

// Tema Ewaffle: lo que la UI mostraba antes de que existiera la marca por org.
export const EWAFFLE_THEME = {
  name: "Ewaffle LMS",
  primaryColor: "#28244a",
  secondaryColor: "#2692d6",
  accentColor: "#fd5a93",
  backgroundColor: "#28244a",
  // Acento de la UI del alumno (botones, tab activa): el azul Tailwind de siempre.
  uiAccent: "#3b82f6",
  uiAccentLight: "#60a5fa",
  faviconUrl: `${process.env.PUBLIC_URL || ""}/wafflechico.png`,
  fontFamily: "Inter",
};

// Lista corta de familias permitidas. Evita inyectar <link> arbitrarios.
export const FONT_OPTIONS = ["Inter", "Roboto", "Outfit", "Raleway", "Poppins", "Montserrat", "Nunito", "Open Sans", "Fredoka", "Libre Franklin"];
const ALWAYS_LOADED_FONTS = new Set(["Inter", "Roboto", "Raleway"]); // ya vienen en index.html

const BrandingContext = createContext({
  org: null,
  branding: {},
  hostOrg: null,
  hostResolved: true,
  isHostOrg: false,
  demo: false,
  brandedApp: false,
  setRouteOrg: () => {},
});

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

/** Host a resolver: variable de entorno para probar una marca en local,
 *  o el hostname real del navegador. */
export const brandHost = () => {
  const forced = process.env.REACT_APP_BRAND_HOST;
  if (forced) return forced.trim().toLowerCase();
  if (typeof window === "undefined") return "";
  return window.location.hostname.toLowerCase();
};

const sessionKey = (host) => `ewaffle:brand-host:${host}`;

const readCache = (host) => {
  try {
    const raw = sessionStorage.getItem(sessionKey(host));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

const writeCache = (host, value) => {
  try {
    sessionStorage.setItem(sessionKey(host), JSON.stringify(value));
  } catch {
    // sin sessionStorage (modo privado estricto) simplemente no cacheamos
  }
};

/** Resuelve la org dueña de `host`. Devuelve null cuando no hay marca para
 *  ese dominio (404), que es el caso normal de app.ewaffle.cl. */
export const resolveOrgByHost = async (host) => {
  if (!host) return null;
  const cached = readCache(host);
  if (cached !== undefined) return cached;
  try {
    const { data } = await axios.get(`${apiUrl}/auth/org/by-host`, { params: { host } });
    writeCache(host, data);
    return data;
  } catch (error) {
    if (error.response?.status === 404) writeCache(host, null);
    return null;
  }
};

const setFavicon = (href) => {
  if (typeof document === "undefined") return;
  ["icon", "apple-touch-icon"].forEach((rel) => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
  });
};

const setThemeColor = (color) => {
  if (typeof document === "undefined") return;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color;
};

const ensureFontLoaded = (family) => {
  if (typeof document === "undefined") return;
  if (!family || ALWAYS_LOADED_FONTS.has(family) || !FONT_OPTIONS.includes(family)) return;
  const id = `brand-font-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
};

/** Escribe la marca en :root. Con `org` null vuelve al tema Ewaffle. */
export const applyBrandToDocument = (org) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const b = org?.branding || {};
  const brandedApp = Boolean(b.brandedApp);
  const font = FONT_OPTIONS.includes(b.fontFamily) ? b.fontFamily : EWAFFLE_THEME.fontFamily;

  root.style.setProperty("--brand-primary", b.primaryColor || EWAFFLE_THEME.primaryColor);
  root.style.setProperty("--brand-secondary", b.secondaryColor || EWAFFLE_THEME.secondaryColor);
  root.style.setProperty("--brand-accent", b.accentColor || EWAFFLE_THEME.accentColor);
  root.style.setProperty("--brand-bg", b.backgroundColor || EWAFFLE_THEME.backgroundColor);
  root.style.setProperty("--brand-font", `"${font}", system-ui, sans-serif`);
  // El acento de la UI del alumno solo cambia cuando la org pidió marca en toda
  // la app; si no, botones y tabs siguen azules como siempre.
  root.style.setProperty(
    "--brand-ui-accent",
    brandedApp ? b.accentColor || EWAFFLE_THEME.uiAccent : EWAFFLE_THEME.uiAccent
  );
  root.style.setProperty(
    "--brand-ui-accent-light",
    brandedApp ? b.secondaryColor || EWAFFLE_THEME.uiAccentLight : EWAFFLE_THEME.uiAccentLight
  );
  root.style.setProperty(
    "--brand-ui-accent-soft",
    `color-mix(in srgb, var(--brand-ui-accent) 6%, transparent)`
  );
  root.dataset.brandedApp = brandedApp ? "true" : "false";
  root.dataset.demo = org?.demo ? "true" : "false";

  ensureFontLoaded(font);
  setFavicon(b.faviconUrl || EWAFFLE_THEME.faviconUrl);
  setThemeColor(brandedApp ? b.backgroundColor || EWAFFLE_THEME.backgroundColor : "#0f172a");
  document.title = org?.name ? `${org.name} · Plataforma de aprendizaje` : EWAFFLE_THEME.name;
};

export const BrandingProvider = ({ children }) => {
  const company = useSelector((state) => state.company);
  const [hostOrg, setHostOrg] = useState(null);
  const [hostResolved, setHostResolved] = useState(false);
  // Org que una pantalla pública ya resolvió por slug (/o/:slug/login): sin
  // sesión y sin dominio propio, igual queremos su marca y su tema.
  const [routeOrg, setRouteOrg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const host = brandHost();
    // En localhost sin REACT_APP_BRAND_HOST no hay nada que resolver.
    if (!host || (LOCAL_HOSTS.has(host) && !process.env.REACT_APP_BRAND_HOST)) {
      setHostResolved(true);
      return undefined;
    }
    resolveOrgByHost(host).then((org) => {
      if (cancelled) return;
      setHostOrg(org);
      setHostResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Un login por slug (/o/:slug/login) muestra SU org aunque el navegador tenga
  // sesión abierta en otra: si no, entrar a oxxo-demo con sesión de aclara-demo
  // pintaba el login de Aclara. OrgLogin limpia routeOrg al desmontarse, así que
  // fuera del login vuelve a mandar la sesión; sin sesión, la del dominio.
  const sessionOrg = company && company._id ? company : null;
  const org = routeOrg || sessionOrg || hostOrg || null;

  useEffect(() => {
    applyBrandToDocument(org);
  }, [org]);

  const value = useMemo(
    () => ({
      org,
      branding: org?.branding || {},
      hostOrg,
      hostResolved,
      isHostOrg: Boolean(hostOrg),
      demo: Boolean(org?.demo),
      brandedApp: Boolean(org?.branding?.brandedApp),
      setRouteOrg,
    }),
    [org, hostOrg, hostResolved]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);

export default BrandingContext;
